import html2canvas from "html2canvas";
import React, { CSSProperties, useRef, useState } from "react";

interface BoxCoordinates {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface SelectionType {
  image: string;
  selectionRegion: BoxCoordinates;
}

interface ClickToSelectProps {
  children: React.ReactNode;
  onSelectionChange: (selection: SelectionType) => void;
}

enum Selection {
  None = "None",
  Clicked = "Clicked",
  Dragging = "Dragging",
  Selected = "Selected",
}

const clamp = (val: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, val));

const ClickToSelect: React.FC<ClickToSelectProps> = ({
  children,
  onSelectionChange,
}) => {
  const [selection, setSelection] = useState(Selection.None);
  const [isSelecting, setIsSelecting] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [{ dx, dy }, setOffset] = useState({
    dx: 0,
    dy: 0,
  });
  const [{ startX, startY }, setStartPosition] = useState({
    startX: 0,
    startY: 0,
  });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e: React.MouseEvent) => {
    // if target is a button, don't start selection
    if (e.target instanceof HTMLButtonElement) return;

    const eleRect = containerRef.current?.getBoundingClientRect();
    if (!eleRect) return;
    const startRelativePos = {
      startX: e.clientX - eleRect.left,
      startY: e.clientY - eleRect.top,
    };
    setOffset({ dx: 0, dy: 0 });
    setStartPosition(startRelativePos);
    setStartPos({ x: e.clientX, y: e.clientY });
    setSelection(Selection.Clicked);
    setIsSelecting(true);
    setShowButton(false);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isSelecting) return;
    const eleRect = containerRef.current?.getBoundingClientRect();
    if (!eleRect) return;
    let dx = e.clientX - startPos.x;
    let dy = e.clientY - startPos.y;
    const maxX = eleRect.width - startX;
    const maxY = eleRect.height - startY;
    dx = clamp(dx, 0, maxX);
    dy = clamp(dy, 0, maxY);

    setOffset({ dx, dy });
    setSelection(Selection.Dragging);
  };

  const onMouseUp = () => {
    setIsSelecting(false);
    setSelection(Selection.Selected);
    setShowButton(true);
  };

  const captureSelection = () => {
    console.log("test");
    console.log(containerRef);
    if (!containerRef.current) return;

    const eleRect = containerRef.current.getBoundingClientRect();

    html2canvas(containerRef.current, {
      x: startX,
      y: startY,
      width: dx,
      height: dy,
    }).then((canvas) => {
      const base64Image = canvas.toDataURL();

      onSelectionChange({
        image: base64Image,
        selectionRegion: {
          left: ((startPos.x - eleRect.left) * 100) / eleRect.width,
          top: ((startPos.y - eleRect.top) * 100) / eleRect.height,
          width: (dx * 100) / eleRect.width,
          height: (dy * 100) / eleRect.height,
        },
      });
      setShowButton(false);
    });
  };

  const onButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log("click");
    captureSelection();
  };

  const selectionBoxStyle: CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    transform: `translate(${startX}px, ${startY}px)`,
    width: `${dx}px`,
    height: `${dy}px`,
    border: "2px dashed rgb(203 213 225)",
    pointerEvents: "none",
  };

  const selectionContainerStyle: CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
    cursor: isSelecting ? "crosshair" : "auto",
  };

  return (
    <div
      ref={containerRef}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      style={selectionContainerStyle}
    >
      {children}
      {(isSelecting || selection === Selection.Selected) && (
        <div style={selectionBoxStyle}></div>
      )}
      {showButton && (
        <button
          onClick={onButtonClick}
          style={{
            position: "absolute",
            left: `${startX}px`,
            top: `${startY + dy}px`,
            zIndex: 2,
          }}
        >
          Highlight this region
        </button>
      )}
    </div>
  );
};

export default ClickToSelect;
